import { useCallback } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ALL_STATUS_OPTIONS } from 'src/constants/status';

// ----------------------------------------------------------------------

type Props = {
  filters: {
    name: string;
    status: number | 0;
  };
  onFilters: (name: string, value: string) => void;
  onResetFilters: VoidFunction;
  canReset: boolean;
  numSelected: number;
  dataCounts: {
    total: number;
    active: number;
    paused: number;
    inactive: number;
  };
};

export default function VuonTrongTableToolbar({
  filters,
  onFilters,
  onResetFilters,
  canReset,
  numSelected,
  dataCounts,
}: Props) {
  const confirm = useBoolean();

  const handleFilterName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('name', event.target.value);
    },
    [onFilters]
  );

  const handleFilterStatus = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      onFilters('status', newValue);
    },
    [onFilters]
  );

  return (
    <>
      <Stack
        spacing={2}
        alignItems={{ xs: 'flex-end', md: 'center' }}
        direction={{
          xs: 'column',
          md: 'row',
        }}
        sx={{
          p: 2.5,
          pr: { xs: 2.5, md: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: 1 }}>
          <TextField
            value={filters.name}
            onChange={handleFilterName}
            placeholder="Tìm kiếm vườn trồng..."
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Làm mới">
            <Button
              variant="outlined"
              onClick={onResetFilters}
              startIcon={<Iconify icon="solar:refresh-bold" />}
              sx={{ minWidth: 100 }}
            >
              Làm mới
            </Button>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          {numSelected > 0 && (
            <Button
              color="error"
              variant="contained"
              onClick={confirm.onTrue}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              sx={{ minWidth: 120 }}
            >
              Xóa ({numSelected})
            </Button>
          )}

          {canReset && ( 
            <Button
              color="error"
              variant="outlined"
              onClick={onResetFilters}
              startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              sx={{ minWidth: 120 }}
            >
              Xóa bộ lọc
            </Button>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={filters.status}
        onChange={handleFilterStatus}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {ALL_STATUS_OPTIONS.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="end"
            value={tab.value}
            label={tab.label}
            icon={
              <Label
                variant={
                   (tab.value === 0 && 'filled') || (tab.value === filters.status ? 'filled' : 'soft')
                }
                color={
                  (tab.value === 1 && 'success') ||
                  (tab.value === 2 && 'warning') ||
                  (tab.value === 3 && 'error') ||
                  'default'
                }
              >
                {tab.value === 0 && dataCounts.total}
                {tab.value === 1 && dataCounts.active}
                {tab.value === 2 && dataCounts.paused}
                {tab.value === 3 && dataCounts.inactive}
              </Label>
            }
          />
        ))}
      </Tabs>

    </>
  );
}
